// oxlint-disable react/jsx-pascal-case
import { type Component, type ComponentProps, type JSX, Show } from "solid-js";
import { cn } from "@habits/shared";
import { HabitsMark } from "./logo";

interface EmptyStateProps {
  icon: Component<ComponentProps<"svg">>;
  /**
   * Optional theme-aware SVG illustration (see ./illustrations). When provided
   * it replaces the icon chip with a larger, motivational graphic; the brand
   * mark still anchors the bottom-right.
   */
  illustration?: Component<ComponentProps<"svg">>;
  title: string;
  description?: string;
  /**
   * Optional CTA(s) rendered below the copy — turns a passive empty state into
   * an actionable first-run moment ("create your first habit").
   */
  action?: JSX.Element;
  /**
   * When provided, the illustrative icon/graphic becomes an interactive target
   * that triggers the same action as the primary CTA (open a dialog, navigate,
   * or focus an input) so the empty state never feels like a dead end. Tapping
   * the icon is a discoverable shortcut to the CTA — leave it undefined for
   * purely informational empty states (the graphic stays decorative).
   */
  onIconActivate?: () => void;
  /**
   * Accessible label for the interactive icon (required when `onIconActivate`
   * is set). Should describe the action, e.g. "Create your first habit".
   */
  iconActionLabel?: string;
  class?: string;
}

export const EmptyState: Component<EmptyStateProps> = (props) => (
  <div class={cn("flex flex-1 flex-col items-center justify-center p-8 text-center", props.class)}>
    <div class="animate-scale-in">
      <Show
        when={props.illustration}
        fallback={
          // When an action is wired, the icon chip becomes a real <button>
          // (native tap/click/Enter/Space keyboard behavior) that triggers the CTA;
          // otherwise, it remains a decorative <div>. It keeps the clone's base
          // classes (accent + brand mark) and adds ONLY focus/hover/motion-safe.
          <Show
            when={props.onIconActivate}
            fallback={
              <div class="relative mb-6 rounded-full bg-accent/5 p-6 ring-1 ring-accent/10 animate-float">
                <props.icon class="h-12 w-12 text-accent/60" />
                {/* Sello de marca: chip sólido accent → siempre legible en todo tema. */}
                <span class="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-sm ring-2 ring-card">
                  <HabitsMark class="h-4 w-4" />
                </span>
              </div>
            }
          >
            {(activate) => (
              <button
                type="button"
                aria-label={props.iconActionLabel ?? props.title}
                onClick={() => {
                  activate()();
                }}
                class="group relative mb-6 rounded-full bg-accent/5 p-6 ring-1 ring-accent/10 animate-float transition-[transform,box-shadow,background-color] duration-200 hover:bg-accent/10 hover:ring-accent/25 hover:shadow-lg hover:shadow-accent/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 motion-safe:hover:scale-105"
              >
                <props.icon class="h-12 w-12 text-accent/60 transition-colors group-hover:text-accent" />
                {/* Sello de marca: chip sólido accent → siempre legible en todo tema. */}
                <span class="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-sm ring-2 ring-card">
                  <HabitsMark class="h-4 w-4" />
                </span>
              </button>
            )}
          </Show>
        }
      >
        {(illustration) => {
          const Illustration = illustration();
          return (
            <Show
              when={props.onIconActivate}
              fallback={
                <div class="relative mb-6 animate-float">
                  <Illustration class="h-32 w-32 text-accent" />
                  {/* Sello de marca: chip sólido accent → siempre legible en todo tema. */}
                  <span class="absolute bottom-1 right-1 flex h-7 w-7 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-sm ring-2 ring-card">
                    <HabitsMark class="h-4 w-4" />
                  </span>
                </div>
              }
            >
              {(activate) => (
                <button
                  type="button"
                  aria-label={props.iconActionLabel ?? props.title}
                  onClick={() => {
                    activate()();
                  }}
                  class="group relative mb-6 rounded-2xl animate-float p-1 transition-[transform] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 motion-safe:hover:scale-105"
                >
                  <Illustration class="h-32 w-32 text-accent transition-opacity group-hover:opacity-90" />
                  {/* Sello de marca: chip sólido accent → siempre legible en todo tema. */}
                  <span class="absolute bottom-1 right-1 flex h-7 w-7 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-sm ring-2 ring-card">
                    <HabitsMark class="h-4 w-4" />
                  </span>
                </button>
              )}
            </Show>
          );
        }}
      </Show>
    </div>
    <h3 class="text-lg font-semibold tracking-tight">{props.title}</h3>
    <Show when={props.description}>
      <p class="mt-2 text-sm text-muted-foreground max-w-xs mx-auto">{props.description}</p>
    </Show>
    <Show when={props.action}>
      <div class="mt-5 animate-scale-in">{props.action}</div>
    </Show>
  </div>
);
